import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Download, Share } from 'lucide-react';
import { useInstallPWA } from './useInstallPWA';

/**
 * Modal con instrucciones de instalación adaptadas a la plataforma:
 * - iOS: pasos manuales con Share → Añadir a pantalla inicio.
 * - Android: botón que dispara el prompt nativo del navegador.
 * - Desktop: instrucciones para Chrome/Edge (icono de instalación en la URL bar).
 *
 * Uso: se abre desde el prompt automático (primera ejecución) o desde
 * Perfil → "Instalar app". Cerrarlo lo dismissa sin marcar nada persistente.
 */
export function InstallInstructionsModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { platform, installed, canPromptNative, promptInstall } = useInstallPWA();

  if (installed) {
    return (
      <ModalShell onClose={onClose} label={t('install.already_installed')}>
        <div className="mb-3 flex items-center gap-2">
          <Download size={20} className="text-positive" />
          <h3 className="font-serif text-lg text-brand">{t('install.already_installed')}</h3>
        </div>
        <p className="text-muted text-sm">{t('install.already_installed_desc')}</p>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={onClose} label={t('install.title')}>
      <div className="mb-3 flex items-center gap-2">
        <Download size={20} className="text-brand" />
        <h3 className="font-serif text-lg text-brand">{t('install.title')}</h3>
      </div>
      <p className="text-muted mb-4 text-sm">{t('install.why')}</p>

      {platform === 'ios' && <IOSInstructions />}
      {platform === 'android' && (
        <AndroidInstructions canPrompt={canPromptNative} onPrompt={promptInstall} />
      )}
      {platform === 'desktop' && <DesktopInstructions />}
      {platform === 'unknown' && <DesktopInstructions />}
    </ModalShell>
  );
}

/**
 * La envoltura del modal, **colgada de `document.body`**.
 *
 * El `z-50` no vale de nada si el modal se pinta dentro de algo que ya crea su
 * propio contexto de apilamiento, y eso es justo lo que pasa desde que se
 * ofrece instalar también desde la lateral: `<aside>` es `fixed` con `z-40`, o
 * sea que el 50 de aquí dentro compite en el 40 de fuera. Abierto desde el
 * globo, que vive en la raíz, el modal salía por delante de todo; abierto desde
 * el menú, no.
 *
 * El portal arregla además una segunda cosa: la lateral es `hidden lg:flex`, así
 * que estrechar la ventana con el modal abierto lo hacía desaparecer —y volver
 * al ensanchar— sin que nadie lo cerrara.
 *
 * Va aquí y no en cada sitio que lo abre: quien lo monta no tiene por qué saber
 * en qué contexto de apilamiento está.
 */
/**
 * `label` da nombre al diálogo, y no es adorno: sin `role="dialog"` un lector de
 * pantalla anuncia esto como un `div` cualquiera y quien no ve la pantalla no se
 * entera de que se ha abierto nada. `ShortcutsHelp` ya lo hacía así; aquí
 * faltaba. Se pasa desde fuera porque el modal tiene dos caras —instrucciones y
 * «ya está instalada»— y cada una se llama distinto. Ninguna cadena nueva: las
 * dos ya existen como título.
 */
function ModalShell({
  children,
  onClose,
  label,
}: {
  children: React.ReactNode;
  onClose: () => void;
  label: string;
}) {
  const { t } = useTranslation();
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end bg-black/40"
      onClick={onClose}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
      tabIndex={-1}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className="w-full rounded-t-2xl bg-white p-4 shadow-2xl"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 20px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">{children}</div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="text-muted hover:text-brand ml-2 shrink-0"
          >
            <X size={20} />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function IOSInstructions() {
  const { t } = useTranslation();
  return (
    <ol className="space-y-3">
      <Step number={1}>
        {t('install.ios_step_1')}
        <span className="text-muted mx-1 inline-flex items-center gap-1">
          (<Share size={14} />)
        </span>
      </Step>
      <Step number={2}>{t('install.ios_step_2')}</Step>
      <Step number={3}>{t('install.ios_step_3')}</Step>
      <p className="text-muted mt-2 text-xs italic">{t('install.ios_note')}</p>
    </ol>
  );
}

function AndroidInstructions({
  canPrompt, onPrompt,
}: {
  canPrompt: boolean;
  onPrompt: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}) {
  const { t } = useTranslation();
  if (canPrompt) {
    return (
      <div className="space-y-3">
        <p className="text-sm">{t('install.android_direct')}</p>
        <button
          type="button"
          onClick={() => void onPrompt()}
          className="btn-primary flex w-full items-center justify-center gap-2"
        >
          <Download size={16} />
          {t('install.install_now')}
        </button>
      </div>
    );
  }
  return (
    <ol className="space-y-3">
      <Step number={1}>{t('install.android_step_1')}</Step>
      <Step number={2}>{t('install.android_step_2')}</Step>
      <Step number={3}>{t('install.android_step_3')}</Step>
      <p className="text-muted mt-2 text-xs italic">{t('install.android_note')}</p>
    </ol>
  );
}

function DesktopInstructions() {
  const { t } = useTranslation();
  return (
    <ol className="space-y-3">
      <Step number={1}>{t('install.desktop_step_1')}</Step>
      <Step number={2}>{t('install.desktop_step_2')}</Step>
      <p className="text-muted mt-2 text-xs italic">{t('install.desktop_note')}</p>
    </ol>
  );
}

function Step({ number, children }: { number: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand text-xs font-semibold text-white">
        {number}
      </span>
      <div className="text-sm">{children}</div>
    </li>
  );
}
